import React, { useState, useEffect, Fragment } from 'react';
import { Search, Filter, Plus, Edit, Trash2, Eye, MoreHorizontal, Send } from 'lucide-react';
import { Menu, Transition } from '@headlessui/react';
import { adminService } from '../services/adminService';
import toast from 'react-hot-toast';

const Users = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedInstagram, setSelectedInstagram] = useState('all');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [page, setPage] = useState(1);

  // Modal state
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [showGrantCoinsModal, setShowGrantCoinsModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [notifyForm, setNotifyForm] = useState({
    title: '',
    message: '',
    type: 'info',
  });
  const [grantCoinsAmount, setGrantCoinsAmount] = useState('');
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [grantCoinsLoading, setGrantCoinsLoading] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  const LIMIT = 30;

  useEffect(() => {
    setUsers([]);
    setOffset(0);
    setHasMore(true);
    fetchUsers(0, true);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
          document.documentElement.offsetHeight - 300 &&
        !loading && !isFetchingMore && hasMore
      ) {
        fetchUsers(offset + LIMIT);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line
  }, [loading, isFetchingMore, hasMore, offset]);

  const fetchUsers = async (offsetToFetch = 0, reset = false) => {
    try {
      if (offsetToFetch === 0) setLoading(true);
      else setIsFetchingMore(true);
      const data = await adminService.getUsers({ offset: offsetToFetch, limit: LIMIT });
      let newUsers = [];
      if (data && data.users && Array.isArray(data.users)) {
        newUsers = data.users;
      } else if (Array.isArray(data)) {
        newUsers = data;
      } else {
        setError('Invalid data format received from server');
        toast.error('Invalid data format received');
        newUsers = [];
      }
      if (reset) {
        setUsers(newUsers);
        setOffset(LIMIT);
      } else {
        setUsers(prev => [...prev, ...newUsers]);
        setOffset(prev => prev + LIMIT);
      }
      setHasMore(newUsers.length === LIMIT);
      setError(null);
    } catch (err) {
      if (offsetToFetch === 0) setUsers([]);
      setError(err.message);
      toast.error('Failed to fetch users');
    } finally {
      if (offsetToFetch === 0) setLoading(false);
      else setIsFetchingMore(false);
    }
  };

  // Only use API data, show empty state if no users
  const displayUsers = Array.isArray(users)
    ? users.filter(u => u && u.id && u.email && u.name)
    : [];

  const filteredUsers = displayUsers.filter(user => {
    const matchesSearch =
      (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.id && user.id.toString().includes(searchTerm));
    const matchesStatus = selectedStatus === 'all' || user.status === selectedStatus;
    const matchesRole = selectedRole === 'all' || user.role === selectedRole;
    const matchesInstagram =
      selectedInstagram === 'all' ||
      (selectedInstagram === 'connected' && user.instagramConnected) ||
      (selectedInstagram === 'not_connected' && !user.instagramConnected);
    return matchesSearch && matchesStatus && matchesRole && matchesInstagram;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      suspended: 'bg-red-100 text-red-800',
      banned: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig[status] || statusConfig.inactive}`}>
        {status || 'inactive'}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      Brand: 'bg-blue-100 text-blue-800',
      Influencer: 'bg-purple-100 text-purple-800',
      Admin: 'bg-red-100 text-red-800',
      SMA: 'bg-green-100 text-green-800',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${roleConfig[role] || 'bg-gray-100 text-gray-800'}`}>
        {role || 'Unknown'}
      </span>
    );
  };

  // Action handlers
  const handleView = async (user) => {
    try {
      const details = await adminService.getUserDetails(user.id);
      setSelectedUser(details);
      setShowViewModal(true);
    } catch (err) {
      toast.error('Failed to fetch user details');
    }
  };

  const handleEdit = async (user) => {
    try {
      const details = await adminService.getUserDetails(user.id);
      setSelectedUser(details);
      setShowEditModal(true);
    } catch (err) {
      toast.error('Failed to fetch user details');
    }
  };

  const handleDelete = async (user) => {
    if (window.confirm(`Delete user "${user.name}"? This cannot be undone.`)) {
      try {
        await adminService.deleteUser(user.id);
        toast.success('User deleted');
        fetchUsers();
      } catch (err) {
        toast.error('Failed to delete user');
      }
    }
  };

  const handleBan = async (user) => {
    if (window.confirm(`Ban user "${user.name}"?`)) {
      try {
        await adminService.changeUserStatus({ userId: user.id, status: 'banned' });
        toast.success('User banned');
        fetchUsers();
      } catch (err) {
        toast.error('Failed to ban user');
      }
    }
  };

  const handleUnban = async (user) => {
    if (window.confirm(`Unban user "${user.name}"?`)) {
      try {
        await adminService.changeUserStatus({ userId: user.id, status: 'active' });
        toast.success('User unbanned');
        fetchUsers();
      } catch (err) {
        toast.error('Failed to unban user');
      }
    }
  };

  const handlePromote = async (user) => {
    if (window.confirm(`Promote "${user.name}" to Admin?`)) {
      try {
        await adminService.updateUser(user.id, { role: 'Admin' });
        toast.success('User promoted to Admin');
        fetchUsers();
      } catch (err) {
        toast.error('Failed to promote user');
      }
    }
  };

  const handleDemote = async (user) => {
    if (window.confirm(`Demote "${user.name}" to Brand?`)) {
      try {
        await adminService.updateUser(user.id, { role: 'Brand' });
        toast.success('User demoted to Brand');
        fetchUsers();
      } catch (err) {
        toast.error('Failed to demote user');
      }
    }
  };

  const handleGrantCoins = (user) => {
    setSelectedUser(user);
    setGrantCoinsAmount('');
    setShowGrantCoinsModal(true);
  };

  const handleGrantCoinsSubmit = async (e) => {
    e.preventDefault();
    setGrantCoinsLoading(true);
    try {
      await adminService.grantCoins(selectedUser.id, grantCoinsAmount);
      toast.success('Coins granted!');
      setShowGrantCoinsModal(false);
      fetchUsers();
    } catch (err) {
      toast.error('Failed to grant coins');
    } finally {
      setGrantCoinsLoading(false);
    }
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setResetPasswordValue('');
    setShowResetPasswordModal(true);
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    setResetPasswordLoading(true);
    try {
      await adminService.resetUserPassword(selectedUser.id, resetPasswordValue);
      toast.success('Password reset!');
      setShowResetPasswordModal(false);
    } catch (err) {
      toast.error('Failed to reset password');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleImpersonate = async (user) => {
    try {
      const res = await adminService.impersonateUser(user.id);
      if (res.token) {
        window.open(`https://pairpinnacle.com/impersonate?token=${res.token}`, '_blank');
      } else {
        toast.error('Failed to impersonate user: No token received.');
      }
    } catch (err) {
      toast.error('Failed to impersonate user');
    }
  };

  const handleNotifyUser = (user) => {
    setSelectedUser(user);
    setNotifyForm({
      title: '',
      message: '',
      type: 'info',
    });
    setShowNotifyModal(true);
  };

  const handleSendUserNotification = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setNotifyLoading(true);
    try {
      await adminService.notifyUser(
        selectedUser.id,
        notifyForm.message,
        notifyForm.title,
        notifyForm.type
      );
      toast.success('Notification sent to user!');
      setShowNotifyModal(false);
    } catch (err) {
      toast.error('Failed to send notification');
    } finally {
      setNotifyLoading(false);
    }
  };

  const closeModal = () => {
    setShowViewModal(false);
    setShowEditModal(false);
    setShowNotifyModal(false);
    setShowGrantCoinsModal(false);
    setShowResetPasswordModal(false);
    setShowImpersonateModal(false);
    setSelectedUser(null);
  };

  // Example handlers for dropdown actions
  const handleBanUser = async (user) => { toast(`Ban user ${user.name}`); };
  const handleUnbanUser = async (user) => { toast(`Unban user ${user.name}`); };
  const handlePromoteUser = async (user) => { toast(`Promote ${user.name} to Admin`); };
  const handleDemoteUser = async (user) => { toast(`Demote ${user.name} to Brand`); };
  const handleResetUserPassword = async (user) => { toast(`Reset password for ${user.name}`); };
  const handleGrantUserCoins = async (user) => { toast(`Grant coins to ${user.name}`); };
  const handleImpersonateUser = async (user) => { toast(`Impersonate ${user.name}`); };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await adminService.updateUser(selectedUser.id, {
        name: selectedUser.name,
        email: selectedUser.email,
        mobile: selectedUser.mobile,
        location: selectedUser.location,
        bio: selectedUser.bio,
      });
      toast.success('User updated!');
      setShowEditModal(false);
      fetchUsers();
    } catch (err) {
      toast.error('Failed to update user');
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    const rounded = Math.round(Number(rating) || 0);
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} style={{ color: i <= rounded ? '#fbbf24' : '#e5e7eb', fontSize: '1.1em' }}>
          ★
        </span>
      );
    }
    return <span>{stars}</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-600">
            {error ? 'Failed to load users' : 'Manage your platform users'}
          </p>
        </div>
        <button className="btn-primary flex items-center">
          <Plus className="h-4 w-4 mr-2" />
          Add User
        </button>
      </div>

      {/* Filters and Search */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="input-field"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
              <option value="banned">Banned</option>
              <option value="pending">Pending</option>
            </select>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="input-field"
            >
              <option value="all">All Roles</option>
              <option value="Brand">Brand</option>
              <option value="Influencer">Influencer</option>
              <option value="Admin">Admin</option>
              <option value="SMA">SMA</option>
            </select>
            <select
              value={selectedInstagram}
              onChange={(e) => setSelectedInstagram(e.target.value)}
              className="input-field"
            >
              <option value="all">All Instagram</option>
              <option value="connected">Connected</option>
              <option value="not_connected">Not Connected</option>
            </select>
            <button className="btn-secondary flex items-center">
              <Filter className="h-4 w-4 mr-2" />
              More Filters
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="card p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="card p-6 mb-6">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button onClick={fetchUsers} className="btn-primary">
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Users Table */}
      {!loading && !error && (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Coins
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rating
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Active
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Instagram
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-6 py-4 text-center text-gray-500">
                      No users found.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary-700">
                              {user.name?.charAt(0)}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(user.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getRoleBadge(user.role)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.coins || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.rating !== null && user.rating !== undefined
                          ? (
                            <span>
                              {renderStars(user.rating)}
                              <span className="ml-2 text-xs text-gray-500">{user.rating}</span>
                            </span>
                          )
                          : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.lastActive ? new Date(user.lastActive).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.instagramConnected
                          ? <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">Connected</span>
                          : <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">Not Connected</span>
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button className="text-primary-600 hover:text-primary-900" onClick={() => handleView(user)}>
                            <Eye className="h-4 w-4" />
                          </button>
                          <button className="text-blue-600 hover:text-blue-900" onClick={() => handleEdit(user)}>
                            <Edit className="h-4 w-4" />
                          </button>
                          <button className="text-red-600 hover:text-red-900" onClick={() => handleDelete(user)}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                          <button className="text-pink-600 hover:text-pink-900" onClick={() => handleNotifyUser(user)}>
                            <Send className="h-4 w-4" />
                          </button>
                          {/* More Actions Dropdown */}
                          <Menu as="div" className="relative inline-block text-left">
                            <Menu.Button className="text-gray-600 hover:text-gray-900">
                              <MoreHorizontal className="h-4 w-4" />
                            </Menu.Button>
                            <Transition
                              as={Fragment}
                              enter="transition ease-out duration-100"
                              enterFrom="transform opacity-0 scale-95"
                              enterTo="transform opacity-100 scale-100"
                              leave="transition ease-in duration-75"
                              leaveFrom="transform opacity-100 scale-100"
                              leaveTo="transform opacity-0 scale-95"
                            >
                              <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                <div className="py-1 flex flex-col">
                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        className={`w-full text-left px-4 py-2 text-sm ${active ? 'bg-gray-100' : ''}`}
                                        onClick={() => handleBan(user)}
                                      >
                                        Ban User
                                      </button>
                                    )}
                                  </Menu.Item>
                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        className={`w-full text-left px-4 py-2 text-sm ${active ? 'bg-gray-100' : ''}`}
                                        onClick={() => handleUnban(user)}
                                      >
                                        Unban User
                                      </button>
                                    )}
                                  </Menu.Item>
                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        className={`w-full text-left px-4 py-2 text-sm ${active ? 'bg-gray-100' : ''}`}
                                        onClick={() => handlePromote(user)}
                                      >
                                        Promote to Admin
                                      </button>
                                    )}
                                  </Menu.Item>
                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        className={`w-full text-left px-4 py-2 text-sm ${active ? 'bg-gray-100' : ''}`}
                                        onClick={() => handleDemote(user)}
                                      >
                                        Demote to Brand
                                      </button>
                                    )}
                                  </Menu.Item>
                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        className={`w-full text-left px-4 py-2 text-sm ${active ? 'bg-gray-100' : ''}`}
                                        onClick={() => handleGrantCoins(user)}
                                      >
                                        Grant Coins
                                      </button>
                                    )}
                                  </Menu.Item>
                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        className={`w-full text-left px-4 py-2 text-sm ${active ? 'bg-gray-100' : ''}`}
                                        onClick={() => handleResetPassword(user)}
                                      >
                                        Reset Password
                                      </button>
                                    )}
                                  </Menu.Item>
                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        className={`w-full text-left px-4 py-2 text-sm ${active ? 'bg-gray-100' : ''}`}
                                        onClick={() => handleImpersonate(user)}
                                      >
                                        Impersonate User
                                      </button>
                                    )}
                                  </Menu.Item>
                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        className={`w-full text-left px-4 py-2 text-sm ${active ? 'bg-gray-100' : ''}`}
                                        onClick={() => handleNotifyUser(user)}
                                      >
                                        Send Notification
                                      </button>
                                    )}
                                  </Menu.Item>
                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        className={`w-full text-left px-4 py-2 text-sm text-red-600 ${active ? 'bg-gray-100' : ''}`}
                                        onClick={() => handleDelete(user)}
                                      >
                                        Delete User
                                      </button>
                                    )}
                                  </Menu.Item>
                                </div>
                              </Menu.Items>
                            </Transition>
                          </Menu>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Infinite Scroll Loading Indicator */}
          {isFetchingMore && (
            <div className="p-4 text-center text-gray-500">Loading more users...</div>
          )}
          {!hasMore && users.length > 0 && (
            <div className="p-4 text-center text-gray-400">No more users to load.</div>
          )}
        </div>
      )}

      {/* View User Modal */}
      {showViewModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full p-6 relative max-h-[90vh] overflow-y-auto">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
              onClick={closeModal}
            >
              &times;
            </button>
            <h2 className="text-xl font-bold mb-4">{selectedUser.name}</h2>
            
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Basic Information</h3>
                <p><strong>Email:</strong> {selectedUser.email}</p>
                <p><strong>Status:</strong> {getStatusBadge(selectedUser.status)}</p>
                <p><strong>Role:</strong> {getRoleBadge(selectedUser.role)}</p>
                <p><strong>Coins:</strong> {selectedUser.coins || 0}</p>
                <p><strong>Mobile:</strong> {selectedUser.mobile || 'N/A'}</p>
                <p><strong>Location:</strong> {selectedUser.location || 'N/A'}</p>
                <p><strong>Bio:</strong> {selectedUser.bio || 'N/A'}</p>
                <p><strong>Joined:</strong> {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString() : 'N/A'}</p>
                <p><strong>Last Active:</strong> {selectedUser.last_active ? new Date(selectedUser.last_active).toLocaleDateString() : 'N/A'}</p>
                {selectedUser.accountAge !== undefined && (
                  <p><strong>Account Age:</strong> {selectedUser.accountAge} days</p>
                )}
              </div>

              {/* Login Location Information */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Login Location Info</h3>
                {selectedUser.locationInfo ? (
                  <>
                    <p><strong>Last IP:</strong> {selectedUser.locationInfo.ip || 'N/A'}</p>
                    <p><strong>City:</strong> {selectedUser.locationInfo.city || 'N/A'}</p>
                    <p><strong>Region:</strong> {selectedUser.locationInfo.region || 'N/A'}</p>
                    <p><strong>Country:</strong> {selectedUser.locationInfo.country || 'N/A'}</p>
                    {selectedUser.locationInfo.location_string && (
                      <p><strong>Full Location:</strong> {selectedUser.locationInfo.location_string}</p>
                    )}
                    {selectedUser.locationInfo.coordinates && (
                      <p><strong>Coordinates:</strong> {selectedUser.locationInfo.coordinates.lat.toFixed(4)}, {selectedUser.locationInfo.coordinates.lon.toFixed(4)}</p>
                    )}
                    <p><strong>Last Login:</strong> {selectedUser.locationInfo.lastLoginAt ? new Date(selectedUser.locationInfo.lastLoginAt).toLocaleString() : 'N/A'}</p>
                  </>
                ) : (
                  <p className="text-gray-500">No location data available</p>
                )}
              </div>
            </div>

            {/* Statistics */}
            {selectedUser.statistics && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-3">Statistics</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{selectedUser.statistics.totalCampaigns}</p>
                    <p className="text-sm text-gray-600">Campaigns</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{selectedUser.statistics.totalApplications}</p>
                    <p className="text-sm text-gray-600">Applications</p>
                  </div>
                  <div className="text-center p-3 bg-yellow-50 rounded-lg">
                    <p className="text-2xl font-bold text-yellow-600">{selectedUser.statistics.totalCoinsEarned}</p>
                    <p className="text-sm text-gray-600">Coins Earned</p>
                  </div>
                </div>
              </div>
            )}

            {/* Recent Transactions */}
            {selectedUser.recentTransactions && selectedUser.recentTransactions.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-3">Recent Transactions</h3>
                <div className="max-h-32 overflow-y-auto">
                  {selectedUser.recentTransactions.map((transaction, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100">
                      <div>
                        <p className="text-sm font-medium">{transaction.description}</p>
                        <p className="text-xs text-gray-500">{new Date(transaction.created_at).toLocaleDateString()}</p>
                      </div>
                      <span className={`text-sm font-bold ${transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                        {transaction.type === 'credit' ? '+' : '-'}{transaction.amount}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Instagram Insights */}
            {selectedUser.instagramInsights && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-3">Instagram Insights</h3>
                <div className="grid grid-cols-2 gap-4">
                  <p><strong>Followers:</strong> {selectedUser.instagramInsights.followers_count || 'N/A'}</p>
                  <p><strong>Engagement Rate:</strong> {selectedUser.instagramInsights.engagement_rate || 'N/A'}%</p>
                  <p><strong>Avg Likes:</strong> {selectedUser.instagramInsights.avg_likes || 'N/A'}</p>
                  <p><strong>Avg Comments:</strong> {selectedUser.instagramInsights.avg_comments || 'N/A'}</p>
                </div>
              </div>
            )}

            <button className="btn-primary mt-6 w-full" onClick={closeModal}>Close</button>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 relative">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
              onClick={closeModal}
            >
              &times;
            </button>
            <h2 className="text-xl font-bold mb-4">Edit User: {selectedUser.name}</h2>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  className="input-field w-full"
                  value={selectedUser.name || ''}
                  onChange={e => setSelectedUser({ ...selectedUser, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  className="input-field w-full"
                  value={selectedUser.email || ''}
                  onChange={e => setSelectedUser({ ...selectedUser, email: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Mobile</label>
                <input
                  type="text"
                  className="input-field w-full"
                  value={selectedUser.mobile || ''}
                  onChange={e => setSelectedUser({ ...selectedUser, mobile: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Location</label>
                <input
                  type="text"
                  className="input-field w-full"
                  value={selectedUser.location || ''}
                  onChange={e => setSelectedUser({ ...selectedUser, location: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Bio</label>
                <textarea
                  className="input-field w-full"
                  value={selectedUser.bio || ''}
                  onChange={e => setSelectedUser({ ...selectedUser, bio: e.target.value })}
                />
              </div>
              <button type="submit" className="btn-primary w-full">Save Changes</button>
            </form>
          </div>
        </div>
      )}

      {/* Notify User Modal */}
      {showNotifyModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 relative">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
              onClick={() => setShowNotifyModal(false)}
            >
              &times;
            </button>
            <h2 className="text-xl font-bold mb-4">Send Notification to {selectedUser.name}</h2>
            <form onSubmit={handleSendUserNotification} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Title</label>
                <input
                  type="text"
                  className="input-field w-full"
                  value={notifyForm.title}
                  onChange={e => setNotifyForm({ ...notifyForm, title: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Message</label>
                <textarea
                  className="input-field w-full"
                  value={notifyForm.message}
                  onChange={e => setNotifyForm({ ...notifyForm, message: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Type</label>
                <select
                  className="input-field w-full"
                  value={notifyForm.type}
                  onChange={e => setNotifyForm({ ...notifyForm, type: e.target.value })}
                >
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="success">Success</option>
                  <option value="error">Error</option>
                </select>
              </div>
              <button type="submit" className="btn-primary w-full" disabled={notifyLoading}>
                {notifyLoading ? 'Sending...' : 'Send Notification'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Grant Coins Modal */}
      {showGrantCoinsModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 relative">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
              onClick={closeModal}
            >
              &times;
            </button>
            <h2 className="text-xl font-bold mb-4">Grant Coins to {selectedUser.name}</h2>
            <form onSubmit={handleGrantCoinsSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Amount</label>
                <input
                  type="number"
                  className="input-field w-full"
                  value={grantCoinsAmount}
                  onChange={e => setGrantCoinsAmount(e.target.value)}
                  required
                  min={1}
                />
              </div>
              <button type="submit" className="btn-primary w-full" disabled={grantCoinsLoading}>
                {grantCoinsLoading ? 'Granting...' : 'Grant Coins'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 relative">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
              onClick={closeModal}
            >
              &times;
            </button>
            <h2 className="text-xl font-bold mb-4">Reset Password for {selectedUser.name}</h2>
            <form onSubmit={handleResetPasswordSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">New Password</label>
                <input
                  type="password"
                  className="input-field w-full"
                  value={resetPasswordValue}
                  onChange={e => setResetPasswordValue(e.target.value)}
                  required
                />
              </div>
              <button type="submit" className="btn-primary w-full" disabled={resetPasswordLoading}>
                {resetPasswordLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
